/**
 * useFlockPopulation — query + mutation hooks for flock population summaries.
 *
 * Population figures are derived from the live Bird Movement Ledger (only
 * "posted" movements count), so invalidating movements also invalidates
 * flock population automatically.
 *
 * Hooks exported:
 *   useFlockPopulation(filters?)      — list query with optional filters
 *   useUpdateFlockPopulation()        — patch a flock summary record
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryOptions,
} from '@tanstack/react-query';
import {
  fetchFlockPopulation,
  updateFlockPopulation,
  type FlockPopulationFilter,
} from '@/lib/birdRecordsApi';
import { queryKeys } from '@/lib/queryKeys';
import type { FlockPopulationSummary } from '@/app/bird-records/components/birdRecordsData';

// ─── List query ───────────────────────────────────────────────────────────────

export function useFlockPopulation(
  filters?: FlockPopulationFilter,
  options?: Omit<UseQueryOptions<FlockPopulationSummary[]>, 'queryKey' | 'queryFn'>,
) {
  return useQuery<FlockPopulationSummary[]>({
    queryKey: queryKeys.flockPopulation.list(filters as Record<string, unknown>),
    queryFn: () => fetchFlockPopulation(filters),
    ...options,
  });
}

// ─── Update mutation ──────────────────────────────────────────────────────────

export function useUpdateFlockPopulation() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      flockId,
      patch,
    }: {
      flockId: string;
      patch: Partial<FlockPopulationSummary>;
    }) => updateFlockPopulation(flockId, patch),

    // Optimistic update
    onMutate: async ({ flockId, patch }) => {
      await qc.cancelQueries({ queryKey: queryKeys.flockPopulation.all });
      const previousData = qc.getQueriesData<FlockPopulationSummary[]>({
        queryKey: queryKeys.flockPopulation.all,
      });
      qc.setQueriesData<FlockPopulationSummary[]>(
        { queryKey: queryKeys.flockPopulation.all },
        (old) =>
          old?.map((f) => (f.flockId === flockId ? { ...f, ...patch } : f)),
      );
      return { previousData };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousData) {
        context.previousData.forEach(([queryKey, data]) => {
          qc.setQueryData(queryKey, data);
        });
      }
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: queryKeys.flockPopulation.all });
    },
  });
}
